<template>
  <div class="tiledContainer">
    <!--manually weather switcher, for dev purposes-->
    <div class="scene-controls-panel">
      <label>
        <span>{{ $t('tiledSceneWeather') }}</span>
        <input v-model="weather" max="12" min="0" step="1" type="number" />
      </label>
    </div>

    <LoaderDesktop :loading="loading" />

    <div
      ref="scenesWrapper"
      :style="{
        zIndex: loading ? -1 : 0
      }"
      class="scenesWrapper"
      element-loading-background="rgba(0, 0, 0, 0.8)"
    >
      <div id="pixiContainer" ref="pixiContainer" :class="{ 'night-mode': isNightModeOn }" />
    </div>

    <el-dialog
      v-model="showSeasonModal"
      :append-to-body="false"
      :center="true"
      :class="[
        `season-bg-${BigNumber(currentSeasonId!).toNumber()}`,
        {
          minContentWidth:
            seasonModalViewProp === SeasonModalView.end || seasonModalViewProp === SeasonModalView.rewards,
          minHeight: seasonModalViewProp !== SeasonModalView.end && seasonModalViewProp !== SeasonModalView.rewards,
          'new-year': isNewYearTheme && seasonModalViewProp !== SeasonModalView.rewards
        }
      ]"
      :fullscreen="isMobile"
      :title="seasonModalTitle"
      class="tw:flex tw:flex-col seasonModal"
      @closed="
        () => {
          seasonModalViewProp = null;
          resetViewport();
          store.openedBuilding = null;
        }
      "
    >
      <SeasonModal
        :view="seasonModalViewProp"
        @close="showSeasonModal = false"
        @update-modal-header="
          (value: string) => {
            seasonModalTitle = value;
          }
        "
      />
    </el-dialog>

    <el-dialog
      v-model="showBuyPearlModal"
      :append-to-body="true"
      :center="true"
      :class="{ 'ru-locale': $i18n.locale === 'ru' }"
      :fullscreen="isMobile"
      :title="!isCraftingBuilding ? $t(selectedBuilding?.buildingKey || '', { count: 2 }) : undefined"
      class="crafting buyPearlModal buildingPearlModal"
      @closed="
        resetViewport();
        store.currentDistrict = null;
      "
    >
      <BuyPearlModal :is-building="true" :show-buy-pearl-modal="showBuyPearlModal" :token="buyToken" />
    </el-dialog>

    <el-dialog
      v-model="showBuyLicenseModal"
      :append-to-body="true"
      :center="true"
      :class="{ 'ru-locale': $i18n.locale === 'ru' }"
      :fullscreen="isMobile"
      :title="!isCraftingBuilding ? $t(selectedBuilding?.buildingKey || '') : undefined"
      class="crafting"
      @closed="resetViewport()"
    >
      <BuyLicenseModal
        :description="buyTokenDescription"
        :is-building="true"
        :show-buy-license-modal="showBuyLicenseModal"
        :token="buyToken"
      />
    </el-dialog>

    <TavernMainContainer v-if="showTavernModal" v-model="showTavernModal" @closed="resetViewport()" />

    <StorageWrapper
      v-if="isShowStorageModal"
      :description="buyTokenDescription"
      :is-storage-selected="isShowStorageModal"
      @closed="resetViewport('storage')"
    />

    <el-dialog
      v-model="showCraftingModal"
      :append-to-body="true"
      :center="true"
      :class="{ 'ru-locale': $i18n.locale === 'ru' }"
      :fullscreen="isMobile"
      :title="!isCraftingBuilding ? selectedBuilding?.buildingName : undefined"
      class="crafting produce"
      @closed="
        resetViewport();
        store.currentDistrict = null;
      "
    >
      <CraftingModal
        v-if="selectedBuilding"
        :selected-building="selectedBuilding"
        @closed="
          showCraftingModal = false;
          resetViewport();
          store.currentDistrict = null;
        "
      />
    </el-dialog>

    <el-dialog
      v-model="isShowStoryModal"
      :append-to-body="true"
      :center="true"
      :class="{ 'ru-locale': $i18n.locale === 'ru' }"
      :title="$t('historyModalStory')"
      class="history-modal"
      @closed="selectedBuilding = null"
    >
      <history-modal :is-show-story-modal="isShowStoryModal" @run-animation="runAnimation" />
    </el-dialog>

    <el-dialog
      v-model="isShowUserProfileModal"
      :append-to-body="true"
      :center="true"
      :class="{ 'ru-locale': $i18n.locale === 'ru' }"
      :title="$t('profilePageName')"
      class="myProfileModal"
      @closed="
        resetViewport();
        store.currentDistrict = null;
      "
    >
      <MyProfileModal />
    </el-dialog>

    <RealtyModal
      v-model="rent.isManorModalOpen"
      @closed="
        resetViewport();
        store.currentDistrict = null;
      "
    ></RealtyModal>

    <ComposedHalloweenEventVisuals
      :address="address"
      :user-data="userData"
      @pause="
        viewport.pause = true;
        app.ticker.stop();
        console.log('viewport: paused');
      "
      @reset="
        resetViewport();
        zoomOutToDefault(viewport);
      "
      @resume="
        viewport.pause = false;
        app.ticker.start();
        console.log('viewport: resume');
      "
    />

    <SellerModal @closed="handleCloseModal" @opened="handleOpenModal" />

    <DexModal v-if="isShowDexModal" v-model:visible="isShowDexModal" />
  </div>

  <RealtyAnounceModal v-model="rent.$state.announceModalOpen" @closed="resetViewport" />
</template>

<script lang="ts" setup>
import { AdjustmentFilter, type AdjustmentFilterOptions } from '@pixi/filter-adjustment';
import { DropShadowFilter } from '@pixi/filter-drop-shadow';
import { SmoothGraphics as Graphics } from '@pixi/graphics-smooth';
import { useWeb3ModalAccount } from '@web3modal/ethers/vue';
import BigNumber from 'bignumber.js';
import { getAddress, ZeroAddress, ZeroHash } from 'ethers';
import gsap from 'gsap';
import { Spine } from 'pixi-spine';
import type { Viewport } from 'pixi-viewport';
import {
  Application,
  Assets,
  Container,
  DisplayObject,
  FederatedPointerEvent,
  type ITextStyle,
  Matrix,
  Point,
  RenderTexture,
  SCALE_MODES,
  Sprite,
  Text,
  type TextStyle,
  Texture
} from 'pixi.js';
import type { Object, Tile } from 'tiled-tmj-typedefs/types/TiledMap';
import type { UnwrapRef, WatchHandle } from 'vue';
import ComposedHalloweenEventVisuals from '~/components/Halloween/ComposedHalloweenEventVisuals.vue';
import MyProfileModal from '~/components/MyProfileModal.vue';
import useRecipesResponseReformatter from '~/composables/useRecipesResponseReformatter';
import tokens from '~/public/img/tokens';
import { useRentEventStore } from '~/stores/rent-event-store';
import type { ITokenModel, IUserModel } from '~/types/apiService';
import { type TNullable, WebSocketsKeys } from '~/types/common';
import type { BuildingProps, RecipeProps, UnclaimedRecipeProps } from '~/types/crafting';
import { type FlipCoinGame, GameStatus } from '~/types/FlipCoin';
import { SeasonModalView } from '~/types/season';
import {
  BuildingTextures,
  ButtonBG,
  type InteractiveObject,
  type IObject,
  type TiledButton,
  TiledButtons
} from '~/types/tiledScene';
import { isInteractiveObject, isPredictOrNull } from '~/types/typeguards';
import { getPixiWorldSizes } from '~/utils';
import { tiledToPixi } from '~/utils/animations';
import { PalmAnimations, palms, ROUTES_WITHOUT_WEATHER_EFFECTS, Tokens } from '~/utils/constants';
import { apiUrls } from '~/utils/constants/apiUrls';
import { Buildings } from '~/utils/constants/buildings';
import buildingsTypeIcons, { BuildingNotificationIcon } from '~/utils/constants/buildingsTypeIcons';
import { MINING_BUILDINGS } from '~/utils/constants/miningBuildings';

import {
  BUTTON_CLAIM_POSITION_Y,
  BUTTONS_OFFSET_X,
  FIRST_BUTTON_POSITION_Y,
  RADIUS_MEDIUM,
  RADIUS_SMALL,
  SPACE_BETWEEN_BUTTONS,
  STEP_MEDIUM,
  STEP_SMALL
} from '~/utils/constants/tiledScene';
import RealtyAnounceModal from '~/widgets/realty/ui/AnounceModal.vue';
import RealtyModal from '~/widgets/realty/ui/RealtyModal.vue';
import SeasonModal from './Season/SeasonModal.vue';

const halloween = useHalloweenStore();

Object.values(Tokens).forEach((token) => {
  if (typeof tokens[token] === 'string') {
    Assets.add({ alias: token, src: tokens[token] });
  }
  if (Array.isArray(tokens[token])) {
    Assets.add({ alias: token, src: tokens[token][0] });
  }
  if (typeof tokens[token] === 'object' && 'common' in tokens[token]) {
    Assets.add({ alias: token, src: tokens[token].common as string });
  }
});
Assets.add({ alias: BuildingTextures.SellerClosed, src: '/assets/new_scenes/Assets/buildings/market1.png' });
Assets.add({ alias: BuildingTextures.SellerOpened, src: '/assets/new_scenes/Assets/buildings/market1_opened.png' });
Assets.add({ alias: 'doorVideo', src: '/door.mp4' });
Assets.add({
  alias: 'hammerIcon',
  src: '/img/buildingTypeIcons/hammer.png'
});
Assets.add({
  alias: 'icPirateIcon',
  src: '/img/buildingTypeIcons/ic_pirate.png'
});
Assets.add({
  alias: 'cartIcon',
  src: '/img/buildingTypeIcons/cart.png'
});
Assets.add({
  alias: 'selectTokenIcon',
  src: '/img/buildingTypeIcons/Select_token.png'
});
Assets.add({
  alias: 'miningIcon',
  src: '/img/buildingTypeIcons/mining.png'
});
Assets.add({
  alias: 'walletIcon',
  src: '/img/buildingTypeIcons/wallet.png'
});
Assets.add({
  alias: 'houseIcon',
  src: '/img/buildingTypeIcons/rent.png'
});
Assets.add({
  alias: 'leaderboardIcon',
  src: '/img/buildingTypeIcons/Leaderboard.png'
});
Assets.add({ alias: 'flipCoinNotificationIcon', src: '/img/icons/flipCoin.png' });
Assets.add({ alias: 'pusherNotificationIcon', src: '/img/icons/pusher.png' });
Assets.add({ alias: 'sellerNotificationIcon', src: '/img/mobileTopToolbar/sellerIcon.png' });

Assets.add({ alias: 'seller', src: '/assets/video/seller.mp4' });
Assets.add({ alias: 'seller_closed', src: '/assets/video/seller_closed.mp4' });

Assets.add({ alias: 'expeditionIcon', src: '/img/buildingTypeIcons/expedition.png' });

await Assets.load([
  BuildingTextures.SellerOpened,
  BuildingTextures.SellerClosed,
  'seller',
  'seller_closed',
  'doorVideo',
  'hammerIcon',
  'icPirateIcon',
  'cartIcon',
  'houseIcon',
  'selectTokenIcon',
  'miningIcon',
  'walletIcon',
  'leaderboardIcon',
  'flipCoinNotificationIcon',
  'pusherNotificationIcon',
  'sellerNotificationIcon',
  'expeditionIcon',
  ...Object.values(Tokens)
]);

const props = defineProps<{ tilemap: any; animation?: string }>();
const PIXI_WORLD_SIZES = getPixiWorldSizes(document.body as HTMLDivElement, props.tilemap);
const FONT_SIZE = 18;
const TOOLTIP_PADDING_X = 16;
const TOOLTIP_PADDING_Y = 6;
const ONE_MINUTE = 60000;
const { app, viewport } = usePIXIAppInstance(PIXI_WORLD_SIZES, true);

const emit = defineEmits(['runAnimation', 'resetAnimation']);
const store = useMainStore();
const { cachedClaimItems, openedBuilding } = storeToRefs(store);
const buildingsNonifications = useBuildingsNotificationsStore();
const { messages } = storeToRefs(buildingsNonifications);
const route = useRoute();
const { login } = useAuth();
const { isMobile } = useDevice();
const { t, locale } = useI18n();
const { apiUrl, blockchain } = useEnvs();
const { data: sellerData, statusWS, isSellerOpen } = useStreetMerchant();
const { buttonStatus, modalStatus, isSellerOpening } = useStreetMerchantStatus(isSellerOpen);
const halloweenButtonText = useHalloweenStatus();
const { address } = useWeb3ModalAccount();
const rent = useRentEventStore();

const { $websockets: websockets } = useNuxtApp();
const craftingSocket = websockets?.[WebSocketsKeys.craftingActivity];
const flipCoinData = websockets?.[WebSocketsKeys.flipCoinGame];
const { reformatRecipesResponse } = useRecipesResponseReformatter();

const currentSeasonId = inject<Ref<number>>('currentSeasonId');
const playerPosition = inject<Ref<number, number>>('playerPosition');

const timeLeft = inject<{
  seconds: number;
  minutes: number;
  hours: number;
  days: number;
  months: number;
  ended: boolean;
}>('timeLeft');
const seasonsDaysLeftText = inject<Ref<string, string>>('seasonsDaysLeftText');
const isUnclaimedPositions = inject<Ref<boolean, boolean>>('isUnclaimedPositions');

const { data: userData } = useNuxtData<IUserModel>('userData');

const seasonModalTitle = ref(t(`seasonName${BigNumber(currentSeasonId!.value).toString()}`));
const pixiContainer = ref(null);
const scenesWrapper = ref(null);
const selectedBuilding = ref<TNullable<BuildingProps>>(null);
const volumeBeforeMute = ref(store.volume.music);
let stopWaitForUserData: WatchHandle | null = null;
const pendingKey = ref<string | null>(null);
const pendingPayload = ref<BuildingProps | null>(null);
const isZooming = ref(false);
const isEventAnimationZoom = ref(false);

const isCraftingBuilding = computed(
  () =>
    selectedBuilding.value &&
    [
      Buildings.GoldOreSmelter,
      Buildings.Jewelry,
      Buildings.Sawmill,
      Buildings.ToymakerShop,
      Buildings.BlackSmith,
      Buildings.IronOreSmelter,
      Buildings.CeramicWorkshop,
      Buildings.GlassWorkshop,
      Buildings.ConstructionCamp,
      Buildings.WitchHut
    ].includes(selectedBuilding.value?.buildingKey)
);
const showSeasonModal = ref(false);

const { data: _recipes } = await useFetch<RecipeProps[]>(
  () => `/crafting/buildings/${selectedBuilding.value?.buildingHash || ZeroHash}/recipes`,
  {
    baseURL: apiUrl,
    watch: [selectedBuilding],
    key: 'buildingRecipes'
  }
);

const recipes = computed(() => {
  return _recipes.value ? reformatRecipesResponse(_recipes.value) : null;
});

const { data: buildingsWithRecipes } = await useFetch<BuildingProps[]>(() => '/crafting/buildings?withRecipes=true', {
  baseURL: apiUrl,
  watch: [selectedBuilding],
  key: 'buildingsWithRecipes'
});

useFetch<ITokenModel[]>(apiUrls.token.tokens, {
  key: 'all-tokens',
  baseURL: apiUrl,
  immediate: true
});

useBackgroundMusic();

const focusBuildingByHash = (buildingHash: string) => {
  console.log('[TiledScene] focusBuildingByHash searching for:', buildingHash);
  console.log('[TiledScene] Total interactive objects:', interactiveObjects.value.length);

  const found = interactiveObjects.value.find((item) => {
    const props = item?.tileObject?.properties;
    return Array.isArray(props) && props.some((p) => p.name === 'buildingHash' && p.value === buildingHash);
  });

  if (!found) {
    console.log('[TiledScene] Building not found in interactiveObjects');
    return null;
  }
  console.log('[TiledScene] Building found:', found);

  const sprite = found.spriteElement;
  const { x, y } = sprite.position;

  const centerX = x + sprite.width / 2;
  const centerY = y + sprite.height / 2;

  return {
    building: found,
    coords: { x: centerX, y: centerY }
  };
};

const focusBuildingByKey = (buildingKey: string) => {
  console.log('[TiledScene] focusBuildingByKey searching for:', buildingKey);

  const found = interactiveObjects.value.find((item) => {
    const props = item?.tileObject?.properties;
    return Array.isArray(props) && props.some((p) => p.name === 'buildingKey' && p.value === buildingKey);
  });

  if (!found) {
    console.log('[TiledScene] Building not found by key in interactiveObjects');
    return null;
  }
  console.log('[TiledScene] Building found by key:', found);

  const sprite = found.spriteElement;
  const { x, y } = sprite.position;

  const centerX = x + sprite.width / 2;
  const centerY = y + sprite.height / 2;

  return {
    building: found,
    coords: { x: centerX, y: centerY }
  };
};

const goToBuildingByHash = async (hash: string) => {
  console.log('[TiledScene] goToBuildingByHash called with hash:', hash);

  if (hash === selectedBuilding.value?.buildingHash) {
    console.log('[TiledScene] Building already selected, skipping');
    return;
  }

  const buildingData = focusBuildingByHash(hash);
  if (!buildingData) {
    console.log('[TiledScene] Building not found for hash:', hash);
    return;
  }
  console.log('[TiledScene] Building found, animating to coords:', buildingData.coords);
  const { building, coords } = buildingData;
  const buildingName = building.tileObject.properties?.find((property) => property.name === 'buildingName');
  const buildingHash = building.tileObject.properties?.find((property) => property.name === 'buildingHash');
  const buildingKey = building.tileObject.properties?.find((property) => property.name === 'buildingKey');
  const descriptionKey = building.tileObject.properties?.find((property) => property.name === 'descriptionKey');
  const buildingObject = {
    buildingName: buildingName?.value,
    buildingHash: buildingHash?.value,
    buildingKey: buildingKey?.value,
    descriptionKey: descriptionKey?.value
  };

  selectedBuilding.value = null;
  await nextTick();
  viewport.animate({
    time: 1000,
    position: coords,
    ease: 'easeInOutSine',
    removeOnInterrupt: true
  });

  setTimeout(() => {
    selectedBuilding.value = buildingObject as BuildingProps;
  }, 1100);
};

const goToBuildingByKey = async (key: string) => {
  console.log('[TiledScene] goToBuildingByKey called with key:', key);

  if (key === selectedBuilding.value?.buildingKey) {
    console.log('[TiledScene] Building already selected by key, skipping');
    return;
  }

  const buildingData = focusBuildingByKey(key);
  if (!buildingData) {
    console.log('[TiledScene] Building not found for key:', key);
    return;
  }
  console.log('[TiledScene] Building found by key, animating to coords:', buildingData.coords);
  const { building, coords } = buildingData;
  const buildingName = building.tileObject.properties?.find((property) => property.name === 'buildingName');
  const buildingHash = building.tileObject.properties?.find((property) => property.name === 'buildingHash');
  const buildingKey = building.tileObject.properties?.find((property) => property.name === 'buildingKey');
  const descriptionKey = building.tileObject.properties?.find((property) => property.name === 'descriptionKey');
  const buildingObject = {
    buildingName: buildingName?.value,
    buildingHash: buildingHash?.value,
    buildingKey: buildingKey?.value,
    descriptionKey: descriptionKey?.value
  };

  selectedBuilding.value = null;
  await nextTick();
  viewport.animate({
    time: 1000,
    position: coords,
    ease: 'easeInOutSine',
    removeOnInterrupt: true
  });

  setTimeout(() => {
    selectedBuilding.value = buildingObject as BuildingProps;
  }, 1100);
};

const showCraftingModal = computed(
  () =>
    !!(
      selectedBuilding.value &&
      isCraftingBuilding.value &&
      recipes.value?.length &&
      !halloween.$state.isSuppressingCraftingModal
    )
);
const showBuyPearlModal = computed(
  () => !!(selectedBuilding.value && [Buildings.Dock].includes(selectedBuilding.value?.buildingKey))
);

const showBuyLicenseModal = computed(
  () =>
    !!(
      selectedBuilding.value &&
      [
        Buildings.CoalMine,
        Buildings.Plantation,
        Buildings.IronOreMine,
        Buildings.GoldOreMine,
        Buildings.SandQuarry,
        Buildings.ClayQuarry
      ].includes(selectedBuilding.value?.buildingKey)
    )
);

const showTavernModal = computed(
  () => !!(selectedBuilding.value && selectedBuilding.value?.buildingKey === Buildings.Tavern)
);

const onSnapZoomEnd = () => {
  isZooming.value = false;

  if (!pendingKey.value || !pendingPayload.value) return;

  buyTokenDescription.value = t(pendingPayload.value.descriptionKey || '');
  selectedBuilding.value = pendingPayload.value;

  pendingKey.value = null;
  pendingPayload.value = null;
};

watch(
  isSellerOpen,
  (value) => {
    if (value) {
      const message = {
        id: Date.now(),
        message: t('sellerOpened'),
        topic: BuildingNotificationIcon.Seller,
        isShowing: false
      };
      if (!messages.value[Buildings.Seller]) {
        messages.value[Buildings.Seller] = [];
      }
      messages.value[Buildings.Seller].push(message);
    }
  },
  { immediate: true }
);

watch([() => flipCoinData.parsedData, address], ([FCDataNew]) => {
  if (
    FCDataNew?.some((FCData: FlipCoinGame) => {
      const isUpdated =
        getAddress(FCData?.ownerPlayer.userAddress) === getAddress(address.value || ZeroAddress) &&
        FCData?.status === GameStatus.STARTED;

      return isUpdated;
    })
  ) {
    const message = {
      id: Date.now(),
      message: t('flipCoinGameStarted'),
      topic: BuildingNotificationIcon.FlipCoin,
      isShowing: false
    };
    if (!messages.value[Buildings.Tavern]) {
      messages.value[Buildings.Tavern] = [];
    }
    messages.value[Buildings.Tavern].push(message);
  }

  if (
    FCDataNew?.some(
      (FCData: FlipCoinGame) =>
        FCData?.status === GameStatus.COMPLETED &&
        getAddress(FCData?.ownerPlayer.userAddress) === getAddress(address.value || ZeroAddress) &&
        getAddress(FCData?.winner) !== getAddress(address.value || ZeroAddress) &&
        Date.now() - new Date(FCData?.closedAt).getTime() <= ONE_MINUTE
    )
  ) {
    const message = {
      id: Date.now(),
      message: t('coreLose'),
      topic: BuildingNotificationIcon.FlipCoin,
      isShowing: false
    };
    if (!messages.value[Buildings.Tavern]) {
      messages.value[Buildings.Tavern] = [];
    }
    messages.value[Buildings.Tavern].push(message);
  }

  if (
    FCDataNew?.some(
      (FCData: FlipCoinGame) =>
        FCData.status === GameStatus.COMPLETED &&
        getAddress(FCData.ownerPlayer.userAddress) === getAddress(address.value || ZeroAddress) &&
        getAddress(FCData.winner) === getAddress(address.value || ZeroAddress) &&
        Date.now() - new Date(FCData.closedAt).getTime() <= ONE_MINUTE
    )
  ) {
    const message = {
      id: Date.now(),
      message: t('coreWin'),
      topic: BuildingNotificationIcon.FlipCoin,
      isShowing: false
    };
    if (!messages.value[Buildings.Tavern]) {
      messages.value[Buildings.Tavern] = [];
    }
    messages.value[Buildings.Tavern].push(message);
  }
});

watch(
  selectedBuilding,
  () => {
    if (!selectedBuilding.value) {
      store.openedBuilding = null;
      halloween.$state.isSuppressingCraftingModal = false;
      return;
    }
    if (selectedBuilding.value?.buildingKey === Buildings.Townhall) {
      showSeasonModal.value = true;
    }
    if (selectedBuilding.value?.buildingKey === Buildings.Manor) {
      if (localStorage.getItem('rent-announced')) {
        rent.$state.isManorModalOpen = true;
      } else {
        rent.$state.announceModalOpen = true;
      }
    } else {
      rent.$state.isManorModalOpen = false;
    }
    if (selectedBuilding.value?.buildingKey === Buildings.WitchHut) {
      halloween.$state.isWitchWelcomeModalOpen = true;
      halloween.$state.isSuppressingCraftingModal = true;

      setTimeout(() => {
        halloween.$state.isWitchWelcomeModalOpen = false;
        halloween.$state.isSuppressingCraftingModal = false;
      }, 4000);
    } else {
      halloween.$state.isSuppressingCraftingModal = false;
    }
    store.openedBuilding = selectedBuilding.value.buildingKey;
  },
  { deep: true }
);

const { setRandomWeatherValue } = store;

const weather = ref(0);
let spineRenderTextureManager: ISpineRenderTextureManager;
const buyToken = ref(Tokens.pearl);
const buyTokenDescription = ref('');
const loading = ref(false);
const isNightModeOn = ref(false);
const isLightningModeOn = ref(false);
const lightningThresholdA = ref(9980);
const lightningThresholdB = ref(9970);
const lightning1 = ref(0);
const lightning2 = ref(0);
const scriptIsRunning = ref(false);
const rainType = ref(0);
const sceneSaturation = ref(0.1);
const rainSprite = ref<Sprite | null>(null);
const lightningInterval = ref(0);
const lightningLayer = ref<Sprite | null>(null);
const currentLightningOpacityValue = ref(0);
const interactiveObjects = ref<InteractiveObject[]>([]);
const buildingNameSprites = ref<Text[]>([]);
const tooltipWrappers = ref<Graphics[]>([]);
const isShowStoryModal = ref(false);
const isShowStorageModal = ref(false);
const isShowUserProfileModal = ref(false);

const isShowSellerModal = ref(false);
const isShowDexModal = ref(false);
const seasonModalViewProp = ref<TNullable<SeasonModalView>>(null);
const buttons = ref<TiledButton[]>([]);

const isNewYearTheme = computed(() => store.isNewYearTheme);

const buttonDexPositionY = computed(() =>
  !isUnclaimedPositions?.value ? BUTTON_CLAIM_POSITION_Y : BUTTON_CLAIM_POSITION_Y + STEP_MEDIUM
);

const buttonSellerPositionY = computed(() => buttonDexPositionY.value + STEP_MEDIUM + SPACE_BETWEEN_BUTTONS);

const buttonHalloweenPositionY = computed(() => buttonSellerPositionY.value + STEP_MEDIUM + SPACE_BETWEEN_BUTTONS);

const buttonStoryPositionY = computed(() => buttonHalloweenPositionY.value + STEP_MEDIUM + SPACE_BETWEEN_BUTTONS);

const buttonStoragePositionY = computed(() => buttonStoryPositionY.value + STEP_SMALL);

const buttonSellerOffsetY = computed(() => {
  if (isSellerOpen.value || isSellerOpening.value) {
    return 0;
  }

  return 20;
});
const BUTTON_MUTE_OFFSET_X = app.renderer.width - 105;

const muteBtnIconKey = computed(() => (store.volume.music === 0 ? TiledButtons.Mute : TiledButtons.Volume));

const daysLeftLabel = computed(() => `${!timeLeft?.ended ? timeLeft?.days : 0} ${seasonsDaysLeftText?.value}`);

const createButtons = async (): Promise<void> => {
  const buttonSeasonContainer = await useSideBarButton(
    app,
    BUTTONS_OFFSET_X,
    ref(FIRST_BUTTON_POSITION_Y),
    RADIUS_MEDIUM,
    ref(TiledButtons.SeasonCup),
    () => {
      showSeasonModal.value = true;
      store.openedBuilding = Buildings.Townhall;
    },
    ButtonBG.primary,
    daysLeftLabel,
    playerPosition,
    'top',
    isNewYearTheme
  );

  const buttonClaimContainer = await useSideBarButton(
    app,
    BUTTONS_OFFSET_X,
    ref(BUTTON_CLAIM_POSITION_Y),
    RADIUS_MEDIUM,
    ref(TiledButtons.SeasonChest),
    () => {
      seasonModalTitle.value = t(`seasonName${BigNumber(currentSeasonId!.value).minus(1).toString()}`);
      seasonModalViewProp.value = SeasonModalView.rewards;
      showSeasonModal.value = true;
      store.openedBuilding = Buildings.Townhall;
    },
    ButtonBG.highlighted,
    undefined,
    undefined,
    undefined,
    isNewYearTheme
  );

  const buttonDexContainer = await useSideBarButton(
    app,
    BUTTONS_OFFSET_X,
    buttonDexPositionY,
    RADIUS_MEDIUM,
    ref(TiledButtons.Dex),
    () => {
      isShowDexModal.value = true;
      store.openedBuilding = Buildings.Dex;

      selectedBuilding.value = {
        buildingName: 'DEX',
        buildingKey: Buildings.Dex,
        buildingHash: '',
        descriptionKey: 'dex_description'
      } as BuildingProps;
    },
    ButtonBG.primary,
    ref(t('dexTitle')),
    undefined,
    'top-inner'
  );

  const buttonSellerContainer = await useSideBarButton(
    app,
    BUTTONS_OFFSET_X,
    buttonSellerPositionY,
    RADIUS_MEDIUM,
    ref(TiledButtons.Seller),
    () => {
      isShowSellerModal.value = true;
      store.openedBuilding = Buildings.Seller;
    },
    ButtonBG.primary,
    buttonStatus,
    undefined,
    'top-inner',
    undefined,
    undefined,
    buttonSellerOffsetY,
    isSellerOpen,
    isShowSellerModal
  );

  const buttonHalloweenContainer = await useSideBarButton(
    app,
    BUTTONS_OFFSET_X,
    buttonHalloweenPositionY,
    RADIUS_MEDIUM,
    ref(TiledButtons.Halloween),
    () => {
      halloween.handleAutoModalTriggerAction(userData);
    },
    ButtonBG.halloween,
    halloweenButtonText,
    undefined,
    'top-inner',
    undefined,
    undefined,
    ref(0),
    undefined,
    undefined,
    ref({ width: 1.2, height: 0.8, x: 2, y: 6 })
  );

  const buttonStoryContainer = await useSideBarButton(
    app,
    BUTTONS_OFFSET_X,
    buttonStoryPositionY,
    RADIUS_SMALL,
    ref(TiledButtons.Story),
    () => (isShowStoryModal.value = true),
    ButtonBG.primary
  );

  const buttonStorageContainer = await useSideBarButton(
    app,
    BUTTONS_OFFSET_X,
    buttonStoragePositionY,
    RADIUS_SMALL,
    ref(TiledButtons.Storage),
    () => openStorage(),
    ButtonBG.primary
  );

  const muteButtonContainer = await useSideBarButton(
    app,
    BUTTON_MUTE_OFFSET_X,
    ref(FIRST_BUTTON_POSITION_Y),
    27,
    muteBtnIconKey,
    () => {
      if (typeof store.volume.music !== 'number') return;
      const currentVolume = store.volume.music;
      if (currentVolume > 0) {
        volumeBeforeMute.value = currentVolume;
        store.setMusicVolume(0);
      } else {
        store.setMusicVolume(volumeBeforeMute.value);
      }
    },
    ButtonBG.primary,
    undefined,
    undefined,
    undefined,
    undefined,
    0.3
  );

  const buttonSeason = {
    key: TiledButtons.SeasonCup,
    container: buttonSeasonContainer
  };

  const buttonClaim = {
    key: TiledButtons.SeasonChest,
    container: buttonClaimContainer
  };

  const buttonSeller = {
    key: TiledButtons.Seller,
    container: buttonSellerContainer
  };

  const buttonHalloween = {
    key: TiledButtons.Halloween,
    container: buttonHalloweenContainer
  };

  const buttonStory = {
    key: TiledButtons.Story,
    container: buttonStoryContainer
  };

  const buttonDex = {
    key: TiledButtons.Dex,
    container: buttonDexContainer
  };

  const buttonStorage = {
    key: TiledButtons.Storage,
    container: buttonStorageContainer
  };

  const buttonMute = {
    key: TiledButtons.Mute,
    container: muteButtonContainer
  };

  buttons.value = [
    buttonSeason,
    buttonClaim,
    buttonStory,
    buttonHalloween,
    buttonStorage,
    buttonDex,
    buttonSeller,
    buttonMute
  ];
};
const resetViewport = async (type = 'default') => {
  if (type === 'storage') {
    // only storage path
    return closeStorageModal();
  }

  // default: if there’s nothing selected, do nothing
  if (!selectedBuilding.value) return;

  // otherwise clear & zoom
  deselectBuildingAndResetView();
};

// Resumes rendering and user interactions for the viewport
const resumeInteractions = (viewport: Viewport, app: Application) => {
  if (!viewport || !app?.ticker) {
    console.error('Viewport or app ticker is not available for resuming interactions.');
    return;
  }
  viewport.pause = false;
  app.ticker.start();
};

// Executes a snap-zoom animation to reset the viewport to a default overview
const zoomOutToDefault = (viewport: Viewport) => {
  if (!viewport) {
    console.error('Viewport is not available for zooming.');
    return;
  }
  const overviewZoomFactor = 0.1;

  viewport.snapZoom({
    width: viewport.screenWidth / overviewZoomFactor,
    height: viewport.screenHeight / overviewZoomFactor,
    time: 1000,
    ease: 'easeInOutSine',
    removeOnComplete: true,
    removeOnInterrupt: true
  });
};

// Hides the storage modal, resumes interactions, and resets the view
async function closeStorageModal() {
  // 1. Update state to hide the modal and clear selection
  isShowStorageModal.value = false;
  selectedBuilding.value = null;

  // 2. Resume map interactions using the helper function
  resumeInteractions(viewport, app);

  // 3. Wait for Vue to process DOM updates (e.g., hide the modal)
  await nextTick();

  // 4. Reset the viewport to the default overview
  zoomOutToDefault(viewport);
}

// Deselects the currently selected building and resets the viewport to the overview
const deselectBuildingAndResetView = () => {
  // Guard clause: If nothing is selected, there's nothing to do.
  if (selectedBuilding.value === null) {
    return;
  }

  pendingKey.value = null;
  pendingPayload.value = null;
  isZooming.value = false;

  // 1. Clear the building selection
  selectedBuilding.value = null;

  // 2. Reset the viewport to the default overview
  zoomOutToDefault(viewport);
};

const handleViewportClick = () => {
  if (!isEventAnimationZoom.value) {
    return;
  }

  zoomOutToDefault(viewport);
  isZooming.value = false;

  setTimeout(() => {
    isEventAnimationZoom.value = false;
  }, 1300);
};

const getButton = (key: TiledButtons) => {
  if (!buttons.value.length) {
    return;
  }

  const [button] = buttons.value.filter((button) => button.key === key);
  return toRaw(button.container);
};

async function renderButtons(): Promise<void> {
  await createButtons();

  app.stage.addChild(getButton(TiledButtons.SeasonCup) as Container);
  app.stage.addChild(getButton(TiledButtons.Storage) as Container);
  app.stage.addChild(getButton(TiledButtons.Story) as Container);
  app.stage.addChild(getButton(TiledButtons.Dex) as Container);
  app.stage.addChild(getButton(TiledButtons.Seller) as Container);
  app.stage.addChild(getButton(TiledButtons.Mute) as Container);

  const halloweenButton = getButton(TiledButtons.Halloween);
  if (halloweenButton) {
    app.stage.addChild(halloweenButton as Container);
  }

  if (isUnclaimedPositions?.value) {
    app.stage.addChild(getButton(TiledButtons.SeasonChest) as Container);
  }
}

const refreshButtons = async () => {
  if (!buttons.value.length) {
    return;
  }

  buttons.value.forEach((button) => button.container.destroy());

  await renderButtons();
};

watch(
  isUnclaimedPositions!,
  async (newValue) => {
    if (newValue) {
      await refreshButtons();
    } else {
      app.stage.removeChild(getButton(TiledButtons.SeasonChest) as Container);
      await refreshButtons();
    }
  },
  { immediate: true }
);

setRandomWeatherValue();
weather.value = store.weatherValue;

const intervalWeather = setInterval(() => setRandomWeatherValue(), 1800000);

const isShowWeatherEffects = computed(() => {
  return ROUTES_WITHOUT_WEATHER_EFFECTS.story !== route.name;
});

const TEXT_ELEMENT_PADDING = isNewYearTheme.value ? { x: 120, y: 25 } : { x: 60, y: 25 };

watch(selectedBuilding, async (selectedBuilding) => {
  if (selectedBuilding?.buildingKey === Buildings.Barbershop) {
    if (!userData.value) {
      await login();
      return;
    }

    isShowUserProfileModal.value = true;
  }

  if (selectedBuilding?.buildingKey === Buildings.Forest) {
    halloween.handleExpeditionLocationSelect('forest', () => {
      resetViewport();
      store.openedBuilding = null;
      selectedBuilding = null;
    });
    return;
  }

  if (selectedBuilding?.buildingKey === Buildings.Cave) {
    halloween.handleExpeditionLocationSelect('cave', () => {
      resetViewport();
      store.openedBuilding = null;
      selectedBuilding = null;
    });
    return;
  }

  if (selectedBuilding?.buildingKey === Buildings.Warehouse) {
    isShowStorageModal.value = true;
  }

  if (selectedBuilding?.buildingKey === Buildings.Seller) {
    isShowSellerModal.value = true;
  }
});

watch(selectedBuilding, (newValue) => {
  viewport.pause = !!newValue;

  if (newValue) {
    app.ticker.addOnce(() => {
      app.ticker.stop();
    });
  } else {
    app.ticker.start();
  }
});

watch(isShowDexModal, (newValue) => {
  if (!newValue && selectedBuilding.value?.buildingKey === Buildings.Dex) {
    selectedBuilding.value = null;
  }
});

const weatherEffects = async () => {
  // Apply weather effects based on weather conditions
  if (isPredictOrNull(rainSprite.value)) {
    //@ts-ignore
    app?.stage?.removeChild(rainSprite.value);
  }
  //@ts-ignore
  if (isPredictOrNull<Sprite>(lightningLayer.value)) {
    app?.stage?.removeChild(lightningLayer.value);
  }

  let palmAnimation: PalmAnimations = PalmAnimations.IDLE;

  if (!isNewYearTheme.value) {
    // Clear and calm weather conditions
    if (weather.value == 0) {
      isLightningModeOn.value = false;
      rainType.value = 0;
      sceneSaturation.value = 1;
    }
    if (weather.value == 1) {
      isLightningModeOn.value = false;
      rainType.value = 0;
      sceneSaturation.value = 0.98;
    }
    if (weather.value == 2) {
      isLightningModeOn.value = false;
      rainType.value = 0;
      sceneSaturation.value = 0.95;
    }
    // Mild weather conditions
    if (weather.value == 3) {
      isLightningModeOn.value = false;
      rainType.value = 0;
      sceneSaturation.value = 0.9;
      palmAnimation = PalmAnimations.SLOW;
    }
    if (weather.value == 4) {
      isLightningModeOn.value = false;
      rainType.value = 0;
      sceneSaturation.value = 0.9;
      palmAnimation = PalmAnimations.SLOW;
    }
    if (weather.value == 5) {
      isLightningModeOn.value = false;
      rainType.value = 0;
      sceneSaturation.value = 0.85;
      palmAnimation = PalmAnimations.SLOW;
    }
    if (weather.value == 6) {
      isLightningModeOn.value = false;
      rainType.value = 0;
      sceneSaturation.value = 0.8;
      palmAnimation = PalmAnimations.SLOW;
    }
    // Rainy weather conditions
    if (weather.value == 7) {
      isLightningModeOn.value = true;
      lightningThresholdA.value = 9997;
      lightningThresholdB.value = 9995;
      rainType.value = 1;
      sceneSaturation.value = 0.75;
      palmAnimation = PalmAnimations.NORMAL;
    }
    if (weather.value == 8) {
      isLightningModeOn.value = true;
      lightningThresholdA.value = 9997;
      lightningThresholdB.value = 9995;
      rainType.value = 1;
      sceneSaturation.value = 0.7;
      palmAnimation = PalmAnimations.NORMAL;
    }
    // Heavy rain weather conditions
    if (weather.value == 9) {
      isLightningModeOn.value = true;
      lightningThresholdA.value = 9992;
      lightningThresholdB.value = 9987;
      rainType.value = 2;
      sceneSaturation.value = 0.5;
      palmAnimation = PalmAnimations.FAST;
    }
    if (weather.value == 10) {
      isLightningModeOn.value = true;
      lightningThresholdA.value = 9992;
      lightningThresholdB.value = 9987;
      rainType.value = 2;
      sceneSaturation.value = 0.4;
      palmAnimation = PalmAnimations.FAST;
    }
    // Heavy rain weather conditions
    if (weather.value == 11) {
      isLightningModeOn.value = true;
      lightningThresholdA.value = 9992;
      lightningThresholdB.value = 9987;
      rainType.value = 2;
      sceneSaturation.value = 0.3;
      palmAnimation = PalmAnimations.STORM;
    }
    // Stormy weather conditions
    if (weather.value == 12) {
      isLightningModeOn.value = true;
      lightningThresholdA.value = 9983;
      lightningThresholdB.value = 9975;
      rainType.value = 3;
      sceneSaturation.value = 0.2;
      palmAnimation = PalmAnimations.STORM;
    }
  }

  if (spineRenderTextureManager) {
    spineRenderTextureManager.addSameAnimationToEachSpine({
      animationName: palmAnimation,
      loop: true
    });
  }

  let snowFilterOptions: undefined | Partial<AdjustmentFilterOptions>;
  let snowWeatherAlpha: undefined | number;

  if (isNewYearTheme.value) {
    if (weather.value == 0) {
      isLightningModeOn.value = false;
      rainType.value = 0;
      sceneSaturation.value = 1;
    }
    if (weather.value == 1) {
      isLightningModeOn.value = false;
      rainType.value = 0;
      sceneSaturation.value = 0.98;
    }
    if (weather.value == 2) {
      isLightningModeOn.value = false;
      rainType.value = 0;
      sceneSaturation.value = 0.95;
    }
    // Mild weather conditions
    if (weather.value == 3) {
      isLightningModeOn.value = false;
      rainType.value = 0;
      sceneSaturation.value = 0.9;
    }

    // Calm snow
    if (weather.value >= 4 && weather.value <= 7) {
      snowWeatherAlpha = 0.3;
      sceneSaturation.value = 0.9;
      rainType.value = 4;

      snowFilterOptions = {
        red: 1.3,
        green: 1.5,
        blue: 2.2,
        brightness: 0.9,
        alpha: 1
      };
    }

    // Stormy snow
    if (weather.value >= 8) {
      snowWeatherAlpha = 0.4;
      sceneSaturation.value = 0.7;
      rainType.value = 5;

      snowFilterOptions = {
        red: 1.6,
        green: 1.6,
        blue: 2.2,
        brightness: 0.75,
        alpha: 1
      };
    }
  }

  const saturationFilter = new AdjustmentFilter(
    isNewYearTheme.value
      ? snowFilterOptions
      : {
          saturation: sceneSaturation.value
        }
  );

  viewport.filters = [saturationFilter];

  // !!! Temporary disabled rain, please don`t delete !!!

  if (rainType.value > 0) {
    const defaultAlpha = 0.2;
    const { videoSprite, lightningLayer: lightningLayerValue } = await createWeatherEffectsLayers(
      rainType.value,
      app.stage.width,
      app.stage.height,
      isNewYearTheme.value ? snowWeatherAlpha : defaultAlpha
    );
    // store the current video sprite
    rainSprite.value = videoSprite;
    lightningLayer.value = lightningLayerValue;
    if (lightningLayer.value instanceof Container) app.stage.addChild(lightningLayer.value);
    if (rainSprite.value instanceof Container) app.stage.addChild(rainSprite.value);
  }
};

// Set up the lightning effect by running the lightningEffect method at regular intervals
const setLightningEffect = () => {
  if (isLightningModeOn.value) {
    // Run lightningEffect every 20ms
    lightningInterval.value = Number(setInterval(lightningEffect, 20));
  }
};
// Perform the lightning effect
const lightningEffect = () => {
  // Check if the script is already running to prevent overlapping executions
  if (scriptIsRunning.value) {
    return false; // Return if the script is running to avoid concurrent execution
  } else {
    scriptIsRunning.value = true; // Set the script as running
  }

  // Define maximum opacity values for different lightning elements
  const lightning1max: number = 0.78431372549019607;
  const lightning2max: number = 0.58823529411764708;
  const overallLightningMax: number = 0.88235294117647056;

  // Get lightning opacity thresholds
  const lightning1Treshold: number = lightningThresholdA.value;
  const lightning2Treshold: number = lightningThresholdB.value;

  // Generate random numbers for comparison
  const randomNumber1: number = Math.random() * 10000;
  const randomNumber2: number = Math.random() * 10000;

  // Update lightning opacity based on thresholds and random numbers
  if (randomNumber1 > lightning1Treshold) lightning1.value = lightning1max;
  if (randomNumber2 > lightning2Treshold) lightning2.value = lightning2max;

  // Calculate combined lightning opacity
  let lightningOpacity: number = lightning1.value + lightning2.value;

  // Ensure the combined lightning opacity does not exceed the overall maximum
  if (lightningOpacity > overallLightningMax) {
    lightning1.value *= overallLightningMax / lightningOpacity;
    lightning2.value *= overallLightningMax / lightningOpacity;
    lightningOpacity = lightning1.value + lightning2.value;
  }

  if (lightningLayer.value) {
    // Get the current opacity of the element
    // Check if the current opacity is greater than 0
    if (currentLightningOpacityValue.value > 0 || lightningOpacity > 0) {
      // Adjust the opacity of the element
      lightningLayer.value.filters = [new AdjustmentFilter({ alpha: lightningOpacity })];
      currentLightningOpacityValue.value = lightningOpacity;
    }
  }

  // Update the lightning values for the next tick
  lightning1.value *= 0.5;
  lightning1.value -= 0.003921568627451;
  if (lightning2.value > 0) lightning2.value += 0.196078431372549 - Math.random() * 0.49019607843137247;

  // Clamp the lightning values to ensure they are within the valid range
  lightning1.value = Math.max(0, lightning1.value);
  lightning2.value = Math.max(0, lightning2.value);
  lightning1.value = Math.min(1, lightning1.value);
  lightning2.value = Math.min(1, lightning2.value);

  // Set the script as not running after completion
  scriptIsRunning.value = false;
};

// Watch for changes in the weather value
watch(
  [weather, isNewYearTheme, () => viewport.children.length],
  () => {
    if (isShowWeatherEffects.value) {
      // Apply weather effects
      weatherEffects();
    }
    if (isLightningModeOn.value) {
      setLightningEffect();
    }
    updateSpineAnimationsForWeather(viewport, props.tilemap, weather.value);
  },
  { immediate: true }
);

watch(interactiveObjects, async (newInteractiveObjects) => {
  if (!newInteractiveObjects.length) return;

  newInteractiveObjects.forEach(({ tileObject, spriteElement }) => {
    buildingHoverHandler(tileObject, spriteElement as Spine | Sprite);
    buildingClickHandler(tileObject, spriteElement as Spine | Sprite, spriteElement as Spine | Sprite);
  });

  destroyTooltips();
  await createBuildingsTooltips();
  await refreshButtons();
});

watch(locale, async () => {
  const buildingObjects = interactiveObjects.value.map(({ tileObject }) => tileObject);

  buildingObjects.map((element, i) => {
    buildingNameSprites.value[i].text = getTextStr(element);
    buildingNameSprites.value[i].updateText(true);
  });

  // destroyTooltips();
  // await createBuildingsTooltips(buildingObjects);
  await refreshButtons();
});

watch(props, (newValue) => {
  if (newValue && app.stage && viewport instanceof Container && newValue?.animation) {
    restartAnimation(viewport, props.tilemap, weather.value, newValue.animation);
    emit('resetAnimation');
  }
});

watch(
  [isSellerOpen, interactiveObjects],
  ([isSellerOpenValue, interactiveObjectsValue]) => {
    const sellerBuilding = interactiveObjectsValue?.find((item) => (item.tileObject as IObject).gid === 220);

    if (sellerBuilding && sellerBuilding.spriteElement instanceof Sprite) {
      if (isSellerOpenValue) {
        sellerBuilding.spriteElement.texture = Assets.get<Texture>(BuildingTextures.SellerOpened);
      } else {
        sellerBuilding.spriteElement.texture = Assets.get<Texture>(BuildingTextures.SellerClosed);
      }
    }
  },
  {
    immediate: true
  }
);

onBeforeUnmount(() => {
  if (viewport?.off) {
    viewport.off('snap-zoom-end', onSnapZoomEnd);
    viewport.off('pointerdown', handleViewportClick);
    viewport.off('dblclick', handleViewportClick);
  }

  if (lightningInterval.value) {
    clearInterval(lightningInterval.value);
  }
  clearInterval(intervalWeather);
  document.removeEventListener('wheel', documentWheelListenerHandler);
  window.removeEventListener('resize', documentResizeListenerHandler);
});

onUnmounted(() => {
  app?.destroy();
});

onMounted(async () => {
  await nextTick();

  const spineRTM = new SpineRenderTextureManager(app, palms);
  spineRenderTextureManager = spineRTM;

  document.addEventListener('wheel', documentWheelListenerHandler, { passive: false });
  window.addEventListener('resize', documentResizeListenerHandler);

  loading.value = true;

  if (isNewYearTheme.value) {
    Assets.add({ alias: 'snowflake', src: '/img/newYear/snowflake.svg' });
    await Assets.load(['snowflake']);
  }
  await renderButtons();
  //@ts-ignore
  pixiContainer.value?.appendChild(app.view);

  // Устанавливаем правильные размеры после монтирования
  await nextTick();
  documentResizeListenerHandler();

  // Convert and display Tiled map using Pixi.js
  interactiveObjects.value = await tiledToPixi({
    viewport: viewport as Viewport,
    tilemap: props.tilemap,
    basePath: '/assets/new_scenes/',
    weather: weather.value,
    spineRenderTextureManager
  });

  if (isShowWeatherEffects.value && !lightningInterval.value) {
    // Apply weather effects
    weatherEffects();
    // Set lightning effect
    setLightningEffect();
  }

  if (viewport?.on) {
    viewport.on('snap-zoom-end', onSnapZoomEnd);
    viewport.on('pointerdown', handleViewportClick);
    viewport.on('dblclick', handleViewportClick);
  }

  setTimeout(() => {
    // Set loading state to false
    loading.value = false;
  }, 5000);
});

const findSpineObjectByName = (container: Container, name: string): Spine | null => {
  for (const layers of container.children) {
    if (layers.children) {
      for (const child of layers.children) {
        if (child instanceof Spine && child.name === name) {
          return child;
        }
      }
    }
  }
  return null;
};

const runAnimation = (name: string) => {
  if (app.stage && viewport instanceof Container) {
    isShowStoryModal.value = false;

    const spineObject = findSpineObjectByName(viewport, name);
    let center: Point;

    if (spineObject) {
      center = new Point(spineObject.x + spineObject.width / 2, spineObject.y + spineObject.height / 2);
    } else {
      center = new Point(1000, 4500);
    }

    isZooming.value = true;

    viewport.snapZoom({
      height: 2000,
      center,
      removeOnComplete: true,
      removeOnInterrupt: true,
      time: 3000
    });

    restartAnimation(viewport, props.tilemap, weather.value, name);

    isEventAnimationZoom.value = true;

    emit('runAnimation');
  }
};

const setBuyTokenBySelectedBuilding = (buildingKey: Buildings) => {
  switch (buildingKey) {
    case Buildings.CoalMine:
      buyToken.value = Tokens.coal;
      break;

    case Buildings.Dock:
      buyToken.value = Tokens.pearl;
      break;

    case Buildings.Plantation:
      buyToken.value = Tokens.wood;
      break;

    case Buildings.IronOreMine:
      buyToken.value = Tokens.ironOre;
      break;

    case Buildings.GoldOreMine:
      buyToken.value = Tokens.goldOre;
      break;
    case Buildings.SandQuarry:
      buyToken.value = Tokens.sand;
      break;
    case Buildings.ClayQuarry:
      buyToken.value = Tokens.clay;
      break;
  }
};

const checkSelectedBuildingWithRecipes = (buildingHash: string): boolean => {
  const index = buildingsWithRecipes.value?.findIndex((building) => building.buildingHash === buildingHash);

  return typeof index === 'number' && index > -1 ? true : false;
};

const updateTooltipsWrappersDebounced = useDebounceFn(
  () => {
    buildingNameSprites.value.forEach((textSprite, i) => {
      const tileObject = interactiveObjects.value[i].tileObject;
      const tooltip = tooltipWrappers.value[i];

      if (!tooltip) return;

      drawTooltipRectangle(tileObject, tooltip as Graphics, textSprite as Text);
    });
  },
  150,
  { maxWait: 500 }
);

const requireLoginIfNeeded = async (buildingKey: Buildings): Promise<boolean> => {
  const loginRequiredBuildings = [Buildings.Barbershop, Buildings.Warehouse];

  const needsLogin = loginRequiredBuildings.includes(buildingKey) && !userData.value;

  if (needsLogin) {
    await login();
    await waitForUserData();

    return !!store.userData;
  }
  return true;
};

const resetAllTooltips = () => {
  buildingNameSprites.value?.forEach((s) => (s.alpha = 0));
  tooltipWrappers.value?.forEach((s) => (s.alpha = 0));
  interactiveObjects.value?.forEach(({ spriteElement }) => {
    if (spriteElement) spriteElement.filters = null;
  });
};

const buildingClickHandler = (
  tile: Object | Tile | IObject,
  targetObject: Spine | Sprite | Container,
  buildingToWatchCoordinates: Spine | Sprite
) => {
  const buildingName = tile?.properties?.find((property) => property.name === 'buildingName');
  const buildingHash = tile?.properties?.find((property) => property.name === 'buildingHash');
  const buildingKey = tile?.properties?.find((property) => property.name === 'buildingKey');
  const descriptionKey = tile?.properties?.find((property) => property.name === 'descriptionKey');

  const isBuildingWithRecipes = checkSelectedBuildingWithRecipes(buildingHash?.value as string);

  if (!Object.values(Buildings).includes(buildingKey?.value as any) || (buildingHash?.value && !isBuildingWithRecipes))
    return;

  targetObject.on(
    'pointerdown',
    useDebounceFn(async (e) => {
      e?.stopPropagation?.();
      resetAllTooltips();

      const { x, y } = buildingToWatchCoordinates.position;

      const buildingObject = {
        buildingName: buildingName?.value,
        buildingHash: buildingHash?.value,
        buildingKey: buildingKey?.value,
        descriptionKey: descriptionKey?.value
      };

      const isLoggedIn = await requireLoginIfNeeded(buildingObject.buildingKey as Buildings);
      if (!isLoggedIn) return;

      if (buildingKey?.value && typeof buildingKey.value === 'string') {
        pendingKey.value = buildingKey.value;
      }

      pendingPayload.value = buildingObject as BuildingProps;
      isZooming.value = true;

      setBuyTokenBySelectedBuilding(buildingObject.buildingKey as Buildings);

      viewport.snapZoom({
        height: 500,
        time: 1000,
        removeOnComplete: true,
        removeOnInterrupt: true,
        center: new Point(x + buildingToWatchCoordinates.width / 2, y + buildingToWatchCoordinates.height / 2)
      });
    })
  );
};

const buildingHoverHandler = (tile: Object | Tile, targetObject: Spine | Sprite) => {
  const isHoverable = tile?.properties?.find((property) => property.name === 'isHoverable');

  if (!isHoverable) return;

  const tintColor = tile?.properties?.find((property) => property.name === 'tintColor');
  const index = interactiveObjects.value.findIndex((item) => item.tileObject.id === tile.id);

  targetObject.eventMode = 'dynamic';
  targetObject.cursor = 'pointer';

  targetObject
    .on('mouseover', (e: FederatedPointerEvent) => {
      if (isInteractiveObject(e.currentTarget)) {
        e.currentTarget.filters = [
          new DropShadowFilter({
            color: Number(tintColor?.value) || 0xddcead,
            alpha: 1,
            blur: 15,
            quality: 5,
            pixelSize: [1, 1],
            offset: { x: 0, y: 0 }
          })
        ];

        gsap.to(buildingNameSprites.value[index], { alpha: 1, duration: 0.2, ease: 'power1.out' });
        gsap.to(tooltipWrappers.value[index], { alpha: 1, duration: 0.2, ease: 'power1.out' });
      }
    })
    .on('mouseout', (e: FederatedPointerEvent) => {
      if (isInteractiveObject(e.currentTarget)) {
        e.currentTarget.filters = null;
        gsap.to(buildingNameSprites.value[index], { alpha: 0, duration: 0.2, ease: 'power1.out' });
        gsap.to(tooltipWrappers.value[index], { alpha: 0, duration: 0.2, ease: 'power1.out' });
      }
    });
};

const getTextStr = (element: Object | Tile | IObject): string => {
  const res = (element as Object)?.properties?.find((prop) => prop.name === 'buildingKey')?.value as string;
  return t(res || '');
};

const getProducedTokensInfo = (buildingRecipeActivity: UnclaimedRecipeProps[]) => {
  const { recipesToClaim, totalRecipes, tokensToClaim } = buildingRecipeActivity.reduce(
    (acc, item) => {
      acc.totalRecipes += item.multiplyModifier;

      if (item.unclaimedModifier === 0) return acc;

      acc.recipesToClaim += item.unclaimedModifier;

      for (const product of item.products) {
        if (!acc.tokensToClaim.find((token) => token.tokenAddress === product.tokenAddress)) {
          acc.tokensToClaim.push({
            tokenAddress: product.tokenAddress,
            amount: BigNumber(product.quantityMax).isZero()
              ? `${BigNumber(item.unclaimedModifier).toFormat(0)} - ${BigNumber(item.unclaimedModifier).toFormat(0)}`
              : `${BigNumber(product.quantityMin).multipliedBy(item.unclaimedModifier).toFormat(0)} - ${BigNumber(product.quantityMax).multipliedBy(item.unclaimedModifier).toFormat(0)}`
          });
        } else {
          const tokenIndex = acc.tokensToClaim.findIndex((token) => token.tokenAddress === product.tokenAddress);
          acc.tokensToClaim[tokenIndex].amount = acc.tokensToClaim[tokenIndex].amount
            .split(' - ')
            .map((AmountItem, i) =>
              BigNumber(AmountItem || 0)
                .plus(
                  BigNumber(i === 0 ? product.quantityMin : product.quantityMax).isZero()
                    ? BigNumber(item.unclaimedModifier)
                    : BigNumber(i === 0 ? product.quantityMin : product.quantityMax).multipliedBy(
                        item.unclaimedModifier
                      )
                )
                .toFormat(0)
            )
            .join(' - ');
        }
      }
      return acc;
    },
    { recipesToClaim: 0, totalRecipes: 0, tokensToClaim: [] as { tokenAddress: string; amount: string }[] }
  );

  return { recipesToClaim, totalRecipes, tokensToClaim };
};

const destroyTooltips = () => {
  tooltipWrappers.value.map((tooltip) => {
    tooltip.destroy();
  });
};

const lang = computed(() => (locale.value === 'ru' ? 'PT Serif' : 'Eczar, sans-serif'));
const style: Partial<ITextStyle> | TextStyle = {
  fontSize: `16px`,
  fontWeight: '600',
  fontFamily: lang.value,
  fill: 0xc4c4c4,
  wordWrap: true,
  wordWrapWidth: 200,
  align: 'center'
};
const comingSoonTextSprite = new Text(t('comingSoon'), style);
comingSoonTextSprite.resolution = devicePixelRatio * 2;
const comingSoonContainer = new Container();
comingSoonTextSprite.position = { x: 0, y: 0 };
comingSoonContainer.addChild(comingSoonTextSprite);

const comingSoonTextTexture = RenderTexture.create({
  width: comingSoonTextSprite.width,
  height: comingSoonTextSprite.height
});

watch(
  locale,
  () => {
    comingSoonTextSprite.text = t('comingSoon');
    comingSoonTextSprite.style = { ...style, fontFamily: lang.value };

    comingSoonTextSprite.updateText(true);
    comingSoonTextTexture.resize(Math.ceil(comingSoonTextSprite.width), Math.ceil(comingSoonTextSprite.height));
    comingSoonContainer.removeChildren();
    comingSoonContainer.addChild(comingSoonTextSprite);

    app.renderer.render(comingSoonContainer, { renderTexture: comingSoonTextTexture });
  },
  { immediate: true }
);

watch(showCraftingModal, (val) => {
  if (!val) resetAllTooltips();
});

const createTooltip = (tileObject: Tile | Object, textElement: UnwrapRef<Text>) => {
  const tooltip = new Graphics();

  const propertyValue = tileObject.properties?.find((property) => property.name === 'isActive')?.value;
  const isActive = propertyValue === undefined || String(propertyValue).toLowerCase() !== 'false';
  const buildingKey = tileObject.properties?.find((property) => property.name === 'buildingKey')?.value as string;
  const buildingObject = interactiveObjects.value.find((object) => object.tileObject.id === tileObject.id);
  const comingSoonElement = !isActive ? new Sprite(comingSoonTextTexture) : null;
  if (!isActive && comingSoonElement) {
    comingSoonElement.anchor.x = 0.5;
    const updateComingSoonPosition = () => {
      comingSoonElement.position = {
        x: TOOLTIP_PADDING_X + Math.max(textElement.width, comingSoonTextSprite.width) / 2,
        y: textElement.height + TOOLTIP_PADDING_Y
      };
    };
    app.ticker.add(updateComingSoonPosition);
    tooltip.on('destroyed', () => {
      app.ticker?.remove(updateComingSoonPosition);
    });

    comingSoonElement.zIndex = 4;
    tooltip.addChild(comingSoonElement);
  }

  drawTooltipRectangle(tileObject, tooltip, textElement as Text);

  if (buildingsTypeIcons[buildingKey as Buildings]?.notificationIcon) {
    const notification = new NotificationBanner({
      fillTexture: buildingsTypeIcons[buildingKey as Buildings]?.gradientFillStops,
      textStyle: { fontFamily: lang.value }
    });

    watch(
      () => messages.value?.[buildingKey as Buildings]?.length,
      () => {
        if (!messages.value?.[buildingKey as Buildings]?.length) {
          notification.hide();
          return;
        }
        if (messages.value?.[buildingKey as Buildings]?.some((message) => message.isShowing)) return;

        const messageToShow = messages.value[buildingKey as Buildings]?.[0];
        const message = messageToShow?.message;
        const notificationIcon = messageToShow?.topic;

        const asset = Assets.get(
          buildingsTypeIcons[buildingKey as Buildings]?.notificationIcon?.find((icon) => icon === notificationIcon) ||
            ''
        );

        notification.show(message, { icon: asset, textStyle: { fontFamily: lang.value } });
        messageToShow.isShowing = true;
        setTimeout(() => {
          messages.value[buildingKey as Buildings] = messages.value?.[buildingKey as Buildings]?.filter(
            (message) => message.id !== messageToShow.id
          );
        }, 15000);
      },
      { immediate: true }
    );

    app.ticker.add(() => {
      notification.pivot.x = notification.width / 2;
      notification.position = {
        x: tooltip.x + tooltip.width / 2,
        y: tooltip.y + (tooltip.alpha > 0 ? tooltip.height : 0)
      };
    });

    app.stage.addChild(notification);
  }
  const targetSize = 20;

  const texture = Assets.get<Texture>(buildingsTypeIcons?.[buildingKey as Buildings]?.iconAlias || 'hammerIcon');
  texture.baseTexture.scaleMode = SCALE_MODES.LINEAR;
  const realWidth = texture.baseTexture.realWidth;
  const resolutionFactor = realWidth / targetSize;

  texture.baseTexture.setResolution(resolutionFactor);

  const iconTypeSprite = new Sprite();
  iconTypeSprite.texture = texture;

  iconTypeSprite.width = targetSize;
  iconTypeSprite.height = targetSize;

  const { start, stop } = applyWobbleLoop(iconTypeSprite, app);

  const buildingHash: string = tileObject?.properties?.find((property) => property.name === 'buildingHash')
    ?.value as string;

  const buildingTypeIcon = new MessageBubble({
    children: [iconTypeSprite],
    padding: { x: 6, y: 1 },
    pointer: true,
    fillColor: buildingsTypeIcons[buildingKey as Buildings]?.gradientFillStops,
    strokeColor: buildingsTypeIcons[buildingKey as Buildings]?.borderColor,
    shadowColor: Number(buildingsTypeIcons[buildingKey as Buildings]?.shadowColor),
    nameTooltipHeight: textElement.height + (isActive ? 0 : comingSoonTextSprite.height) + 12
  });

  buildingTypeIcon.zIndex = 0;
  buildingTypeIcon.on('pointerover', () => {
    buildingTypeIcon.zIndex = 61;
    gsap.to(buildingTypeIcon.scale, { x: 1.1, y: 1.1, duration: 0.2, ease: 'power1.out' });
    gsap.to(textElement, { alpha: 1, duration: 0.2, ease: 'power1.out' });
    gsap.to(tooltip, { alpha: 1, duration: 0.2, ease: 'power1.out' });
  });
  buildingTypeIcon.on('pointerout', () => {
    buildingTypeIcon.zIndex = 1;
    gsap.to(buildingTypeIcon.scale, { x: 1.0, y: 1.0, duration: 0.2, ease: 'power1.out' });
    gsap.to(textElement, { alpha: 0, duration: 0.2, ease: 'power1.out' });
    gsap.to(tooltip, { alpha: 0, duration: 0.2, ease: 'power1.out' });
  });
  buildingObject?.spriteElement.on('pointerover', () => {
    buildingTypeIcon.zIndex = 61;
    gsap.to(buildingTypeIcon.scale, { x: 1.1, y: 1.1, duration: 0.2, ease: 'power1.out' });
  });
  buildingObject?.spriteElement.on('pointerout', () => {
    buildingTypeIcon.zIndex = 1;
    gsap.to(buildingTypeIcon.scale, { x: 1.0, y: 1.0, duration: 0.2, ease: 'power1.out' });
  });

  buildingClickHandler(tileObject, buildingTypeIcon, buildingObject?.spriteElement as Spine | Sprite);

  buildingTypeIcon.pivot.x = buildingTypeIcon.width / 2;
  buildingTypeIcon.pivot.y = buildingTypeIcon.height;

  app.stage.addChild(buildingTypeIcon);
  buildingTypeIcon.visible = isActive;

  if (buildingsTypeIcons?.[buildingKey as Buildings]?.iconAlias === 'hammerIcon') {
    const claimMesageNotification = new MessageBubble({
      children: [],
      padding: { x: 6, y: 1 },
      pointer: false,
      fillColor: ['#4EFF6E', '#04202B'],
      strokeColor: '0xffffff',
      nameTooltipHeight: textElement.height + (isActive ? 0 : comingSoonTextSprite.height) + 12
    });

    let currentBuildingCraftingActivity: any[];
    let isProducingNow = false;

    const buildingClaimItems = computed(() => {
      return cachedClaimItems.value.filter((item) =>
        Object.values(item.unclaimedByRecipeIndexes).some((modifier: any) => modifier.buildingHash === buildingHash)
      );
    });

    const buildingModifiers = computed(() => {
      return buildingClaimItems.value.flatMap((item) => Object.values(item.unclaimedByRecipeIndexes));
    });

    app.ticker.add(() => {
      const readyPercentage = calculateQueueRecipeProgress(currentBuildingCraftingActivity);
      buildingTypeIcon.setProgress(readyPercentage);
      if (readyPercentage && readyPercentage < 100) {
        isProducingNow = true;
      } else {
        isProducingNow = false;
      }
    });

    watch(
      [buildingClaimItems, buildingModifiers, address, () => craftingSocket?.parsedData],
      async () => {
        currentBuildingCraftingActivity = buildingClaimItems.value.map((item) => ({
          ...item.recipe,
          unclaimedModifier: item.available,
          buildingHash
        }));

        // If there are new recipes from socket that aren't in current activity yet, add them
        const socketRecipes: UnclaimedRecipeProps[] = craftingSocket?.parsedData || [];
        const existingRecipeIds = new Set(currentBuildingCraftingActivity.map((item) => item.recipeId));

        const newSocketRecipes = socketRecipes.filter(
          (socketItem) => !existingRecipeIds.has(socketItem.recipeId) && socketItem.buildingHash === buildingHash
        );

        newSocketRecipes.forEach((socketItem) => {
          currentBuildingCraftingActivity.push({
            ...socketItem,
            buildingHash
          });
        });

        if (isProducingNow) {
          start();
        } else {
          stop();
        }

        const recipesData = getProducedTokensInfo(currentBuildingCraftingActivity);

        if (recipesData.recipesToClaim === 0) {
          claimMesageNotification.visible = false;
          return;
        }
        const tokenIcons = recipesData.tokensToClaim.map((recipe) => {
          const texture =
            Assets.get<Texture>(tokenMetaData(blockchain.contracts, recipe.tokenAddress).name.contract as Tokens) ||
            Texture.EMPTY;
          const iconSprite = new Sprite();
          texture.baseTexture.scaleMode = SCALE_MODES.LINEAR;
          const realWidth = texture.baseTexture.realWidth;
          const iconResolutionFactor = realWidth / targetSize;

          texture.baseTexture.setResolution(iconResolutionFactor);
          iconSprite.texture = texture;
          iconSprite.width = targetSize;
          iconSprite.height = targetSize;

          const mask = new Graphics();
          mask.beginFill(0xffffff);
          mask.drawRoundedRect(0, 0, targetSize, targetSize, 8);
          mask.endFill();

          // Apply mask to sprite
          iconSprite.mask = mask;

          iconSprite.addChild(mask);

          return iconSprite;
        });

        if (tokenIcons.length === 0) {
          claimMesageNotification.visible = false;
        } else {
          claimMesageNotification.visible = true;
        }

        if (tokenIcons.length === 1) {
          const [min, max] = recipesData.tokensToClaim[0].amount.split(' - ');
          const textContent = BigNumber(min).isEqualTo(max) ? max : `${min} - ${max}`;
          const amountText = new Text(textContent, {
            ...style,
            fill: 0xffffff,
            fontWeight: '700'
          });
          tokenIcons.push(amountText);
        }

        claimMesageNotification.updateChildren(tokenIcons.length > 5 ? tokenIcons.slice(0, 5) : tokenIcons);
        claimMesageNotification.pivot.y = claimMesageNotification.height;
      },
      { immediate: true }
    );

    claimMesageNotification.zIndex = 0;
    buildingTypeIcon.on('pointerover', () => {
      claimMesageNotification.zIndex = 61;
      gsap.to(claimMesageNotification.scale, { x: 1.1, y: 1.1, duration: 0.2, ease: 'power1.out' });
    });
    buildingTypeIcon.on('pointerout', () => {
      claimMesageNotification.zIndex = 1;
      gsap.to(claimMesageNotification.scale, { x: 1.0, y: 1.0, duration: 0.2, ease: 'power1.out' });
    });
    buildingObject?.spriteElement.on('pointerover', () => {
      claimMesageNotification.zIndex = 61;
      gsap.to(claimMesageNotification.scale, { x: 1.1, y: 1.1, duration: 0.2, ease: 'power1.out' });
    });
    buildingObject?.spriteElement.on('pointerout', () => {
      claimMesageNotification.zIndex = 1;
      gsap.to(claimMesageNotification.scale, { x: 1.0, y: 1.0, duration: 0.2, ease: 'power1.out' });
    });

    const updateBuildingTypeIconPositionAndHideInactiveIcons = () => {
      if (buildingNameSprites.value.some((sprite) => sprite.alpha === 1 && sprite !== textElement)) {
        gsap.to(buildingTypeIcon, { alpha: 0, duration: 0.6, ease: 'power1.out' });
      } else if (textElement.alpha === 1 || claimMesageNotification.visible || isProducingNow) {
        gsap.to(buildingTypeIcon, { alpha: 1, duration: 0.6, ease: 'power1.out' });
      } else {
        gsap.to(buildingTypeIcon, {
          alpha: 0.45 + viewport.scale.x,
          duration: 0.6,
          ease: 'power1.out'
        });
      }
      buildingTypeIcon.position = {
        x:
          textElement.x +
          textElement.width / 2 -
          (claimMesageNotification.visible ? claimMesageNotification.width : 0) / 2,
        y: textElement.y - (textElement.alpha === 0 ? 10 : 14 + textElement.height)
      };
    };

    const updateClaimMesageNotificationPositionAndHideInactiveIcons = () => {
      if (buildingNameSprites.value.some((sprite) => sprite.alpha === 1 && sprite !== textElement)) {
        gsap.to(claimMesageNotification, { alpha: 0, duration: 0.6, ease: 'power1.out' });
      } else if (textElement.alpha === 1 || claimMesageNotification.visible || isProducingNow) {
        gsap.to(claimMesageNotification, { alpha: 1, duration: 0.6, ease: 'power1.out' });
      } else {
        gsap.to(claimMesageNotification, {
          alpha: 0.45 + viewport.scale.x,
          duration: 0.6,
          ease: 'power1.out'
        });
      }
      claimMesageNotification.position = {
        x: buildingTypeIcon.x + buildingTypeIcon.width / 2 + 5,
        y: textElement.y - (textElement.alpha === 0 ? 10 : 14 + textElement.height)
      };
    };

    app.ticker.add(updateBuildingTypeIconPositionAndHideInactiveIcons);

    app.ticker.add(updateClaimMesageNotificationPositionAndHideInactiveIcons);

    tooltip.on('destroyed', () => {
      app.ticker?.remove(updateBuildingTypeIconPositionAndHideInactiveIcons);
      app.ticker?.remove(updateClaimMesageNotificationPositionAndHideInactiveIcons);
    });

    app.stage.addChild(claimMesageNotification);
  } else {
    const positionTextSpriteAndHideInactiveIcons = () => {
      if (buildingNameSprites.value.some((sprite) => sprite.alpha === 1 && sprite !== textElement)) {
        gsap.to(buildingTypeIcon, { alpha: 0, duration: 0.6, ease: 'power1.out' });
      } else if (textElement.alpha === 1) {
        gsap.to(buildingTypeIcon, { alpha: 1, duration: 0.6, ease: 'power1.out' });
      } else {
        gsap.to(buildingTypeIcon, {
          alpha: 0.45 + viewport.scale.x,
          duration: 0.6,
          ease: 'power1.out'
        });
      }
      const offsetYForBuildingIcon = isActive ? 0 : textElement.height + TOOLTIP_PADDING_Y;

      buildingTypeIcon.position = {
        x: textElement.x + textElement.width / 2,
        y: textElement.y - (textElement.alpha === 0 ? 10 - offsetYForBuildingIcon : 14 + textElement.height)
      };
    };
    app.ticker.add(positionTextSpriteAndHideInactiveIcons);
    tooltip.on('destroyed', () => app.ticker?.remove(positionTextSpriteAndHideInactiveIcons));
  }

  tooltip.alpha = 0;
  tooltip.sortableChildren = true;

  if (isNewYearTheme.value) {
    const snowflakeHeight = 70;
    const snowflakeWidth = 72;
    const offsetY = 35;
    const snowflakeLeft = Sprite.from('snowflake');
    const snowflakeRight = Sprite.from('snowflake');

    snowflakeLeft.height = snowflakeHeight;
    snowflakeLeft.width = snowflakeWidth;

    snowflakeRight.height = snowflakeHeight;
    snowflakeRight.width = snowflakeWidth;

    snowflakeLeft.position = {
      x: textElement.x - TEXT_ELEMENT_PADDING.x + 20,
      y: tileObject.y - (tileObject.height + TEXT_ELEMENT_PADDING.y * 2 + FONT_SIZE) + offsetY
    };
    snowflakeRight.position = {
      x: textElement.x + textElement.width + 20,
      y: tileObject.y - (tileObject.height + TEXT_ELEMENT_PADDING.y * 2 + FONT_SIZE) + offsetY
    };

    tooltip.addChild(snowflakeLeft);
    tooltip.addChild(snowflakeRight);
  }

  return tooltip;
};

const createBuildingsTooltips = async () => {
  const tooltipsContainer = new Container();
  tooltipsContainer.sortableChildren = true;
  tooltipsContainer.zIndex = 70;
  app.stage.addChild(tooltipsContainer);

  setTextSpritePositions();

  tooltipWrappers.value = interactiveObjects.value.map(({ tileObject }, i) => {
    const textElement = buildingNameSprites.value[i];

    return createTooltip(tileObject, textElement);
  });

  buildingNameSprites.value.forEach((text) => {
    text.zIndex = 5;
    if (text instanceof DisplayObject) tooltipsContainer.addChild(text);
  });
  tooltipWrappers.value.forEach((wrapper) => {
    wrapper.zIndex = 1;
    if (wrapper instanceof DisplayObject) tooltipsContainer.addChild(wrapper);
  });
};

function setTextSpritePositions(): void {
  const lang = locale.value === 'ru' ? 'PT Serif' : 'Eczar, sans-serif';

  buildingNameSprites.value = interactiveObjects.value.map((element) => {
    const style: Partial<ITextStyle> | TextStyle = {
      fontSize: `${FONT_SIZE}px`,
      fontWeight: '600',
      fontFamily: lang,
      fill: 0xffffff,
      wordWrap: true,
      wordWrapWidth: 200,
      align: 'center'
    };

    const textSprite = new Text(getTextStr(element.tileObject), style);

    textSprite.resolution = devicePixelRatio * 2;
    setTextSpritePosition(element, textSprite);
    textSprite.zIndex = 1;
    textSprite.alpha = 0;

    return textSprite;
  });
}

function setTextSpritePosition(building: UnwrapRef<InteractiveObject>, textSprite: Text) {
  textSprite.style.fontFamily = lang.value;
  textSprite.updateText(true);
  const { tileObject, spriteElement } = building;
  const buildingNameXY = viewport.toGlobal(new Point(tileObject.x, tileObject.y));
  const scaleX = viewport?.scale.x;
  const scaleY = viewport?.scale.y;
  const stageHeight = tileObject.height * scaleY;
  const isActive = tileObject.properties?.find((property) => property.name === 'isActive')?.value !== 'false';
  const maxWidth = !isActive ? Math.max(textSprite?.width, comingSoonTextSprite?.width) : textSprite.width;

  const buildingKey = tileObject.properties?.find((p) => p.name === 'buildingKey')?.value;
  const isForestSpine = buildingKey === 'forest' && spriteElement instanceof Spine;

  let centerOffsetX;
  if (isForestSpine) {
    centerOffsetX = (spriteElement.width / 3) * scaleX;
  } else {
    centerOffsetX =
      spriteElement instanceof Spine
        ? ((tileObject.width * spriteElement.scale.x) / 2) * scaleX
        : (spriteElement.width / 2) * scaleX;
  }

  textSprite.position = {
    x: buildingNameXY.x - maxWidth / 2 + centerOffsetX,
    y: buildingNameXY.y - (stageHeight + (isActive ? 0 : comingSoonTextSprite.height))
  };
  textSprite.anchor.y = 1;
}

const updateTextSpritesPositions = () => {
  interactiveObjects.value.forEach((interactiveObject, i) => {
    const buildingNameXY = viewport?.toGlobal(
      new Point(interactiveObject.tileObject.x, interactiveObject.tileObject.y)
    );

    const buildingNameSprite = buildingNameSprites.value[i] as Text;
    const tooltipWrapper = tooltipWrappers.value[i] as Graphics;
    const scaleY = viewport?.scale.y;
    const stageHeight = interactiveObject.tileObject.height * scaleY;

    setTextSpritePosition(interactiveObject, buildingNameSprite);

    const isActive =
      interactiveObject.tileObject.properties?.find((property) => property.name === 'isActive')?.value !== 'false';

    const maxWidth = !isActive
      ? Math.max(buildingNameSprite?.width, comingSoonTextSprite?.width)
      : buildingNameSprite.width;

    tooltipWrapper.position = {
      x: buildingNameSprite.x - TOOLTIP_PADDING_X - maxWidth / 2 + buildingNameSprite.width / 2,
      y: buildingNameXY.y - (stageHeight + TOOLTIP_PADDING_Y + (isActive ? 0 : comingSoonTextSprite.height))
    };
  });
};

app.ticker.add(updateTextSpritesPositions);
app.stage.on('destroyed', () => {
  app.ticker?.remove(updateTextSpritesPositions);
});

function drawTooltipRectangle(tileObject: Tile | Object, tooltip: Graphics, textElement: Text) {
  const paddingX = TOOLTIP_PADDING_X;
  const paddingY = TOOLTIP_PADDING_Y;

  const propertyValue = tileObject.properties?.find((property) => property.name === 'isActive')?.value;
  const isActive = propertyValue === undefined || String(propertyValue).toLowerCase() !== 'false';

  const maxWidth = !isActive ? Math.max(textElement?.width, comingSoonTextSprite?.width) : textElement.width;

  tooltip.pivot.y = textElement.height;

  // Create canvas with radial gradient
  const gradientCanvas = document.createElement('canvas');
  gradientCanvas.width = maxWidth + paddingX * 2;
  gradientCanvas.height = textElement.height + paddingY * 2 + (isActive ? 0 : comingSoonTextSprite.height);

  const ctx = gradientCanvas.getContext('2d')!;

  // Radial gradient: center to transparent edges
  const gradient = ctx?.createRadialGradient(
    (maxWidth + paddingX * 2) / 2,
    textElement.height + (isActive ? 0 : comingSoonTextSprite.height) + 5 + paddingY + 25,
    10,
    (maxWidth + paddingX * 2) / 2,
    textElement.height + (isActive ? 0 : comingSoonTextSprite.height) + 5 + paddingY + 25,
    // Increased radius for smoother fade
    150
  );

  gradient?.addColorStop(0, '#397082'); // center - full color
  gradient?.addColorStop(0.3, '#39708280'); // 30%
  gradient?.addColorStop(0.6, '#39708240'); // 60%
  gradient?.addColorStop(0.8, '#39708220'); // 80%
  gradient?.addColorStop(1, '#00000000'); // edge

  ctx.fillStyle = gradient;
  ctx.fillRect(
    0,
    0,
    maxWidth + paddingX * 4,
    textElement.height + paddingY * 2 + (isActive ? 0 : comingSoonTextSprite.height)
  );

  // Create PIXI texture from gradient canvas
  const gradientTexture = Texture.from(gradientCanvas);

  tooltip.clear();

  tooltip.beginFill(0x081b25);
  tooltip.drawRoundedRect(
    0,
    0,
    maxWidth + paddingX * 2,
    textElement.height + paddingY * 2 + (isActive ? 0 : comingSoonTextSprite.height),
    10
  );
  tooltip.endFill();

  // Draw gradient overlay (same size and position) with smooth border
  tooltip.lineStyle({ width: 1, color: 0xffffff, alpha: 0.15 });
  tooltip.beginTextureFill({
    texture: gradientTexture,
    matrix: new Matrix(1, 0, 0, 1, 0, 0)
  });
  tooltip.drawRoundedRect(
    0,
    0,
    maxWidth + paddingX * 2,
    textElement.height + paddingY * 2 + (isActive ? 0 : comingSoonTextSprite.height),
    10
  );
  tooltip.endFill();
}

viewport.on('moved', updateTooltipsWrappersDebounced);

watch(locale, updateTooltipsWrappersDebounced);

const documentWheelListenerHandler = (e: WheelEvent) => {
  if (e.ctrlKey) {
    e.preventDefault();
    e.stopPropagation();
  }
};

const documentResizeListenerHandler = useDebounceFn(() => {
  const wrapperElement = scenesWrapper.value as unknown as HTMLDivElement;
  if (!wrapperElement) return;

  // Пытаемся получить высоту родительского контейнера .scene-wrapper, который имеет height: calc(100dvh - 117px)
  // Или используем высоту самого wrapper, если он уже имеет правильные размеры
  let availableHeight: number;

  // Сначала пробуем найти родительский .scene-wrapper
  const parentElement = wrapperElement.parentElement?.closest('.scene-wrapper') as HTMLElement;
  if (parentElement && parentElement.clientHeight > 0) {
    availableHeight = parentElement.clientHeight;
  } else if (wrapperElement.clientHeight > 0) {
    // Если родитель не найден, используем высоту самого wrapper
    availableHeight = wrapperElement.clientHeight;
  } else {
    // Fallback: вычисляем как в CSS calc(100dvh - 117px)
    // Вычитаем 80px для получения ~782px (если window.innerHeight = 862px, то 862 - 80 = 782px)
    const dvh = window.innerHeight;
    availableHeight = Math.max(0, dvh - 80);
  }

  const availableWidth = wrapperElement.clientWidth || window.innerWidth;

  const PIXI_WORLD_SIZES = getPixiWorldSizes(wrapperElement, props.tilemap);
  const PIXI_WORLD_SIZES_WIDTH = availableWidth;
  const PIXI_WORLD_SIZES_HEIGHT = availableHeight;

  // Обновляем размеры viewport с учетом реальной доступной высоты
  if (PIXI_WORLD_SIZES.viewportSizes) {
    viewport?.resize(
      PIXI_WORLD_SIZES_WIDTH,
      PIXI_WORLD_SIZES_HEIGHT,
      PIXI_WORLD_SIZES.viewportSizes.worldWidth,
      PIXI_WORLD_SIZES.viewportSizes.worldHeight
    );
  }

  // Размер рендерера должен соответствовать доступной высоте
  app?.renderer?.resize(PIXI_WORLD_SIZES_WIDTH, PIXI_WORLD_SIZES_HEIGHT);

  if (viewport?.center) {
    const center = viewport.center;
    viewport.moveCenter(center.x, center.y);
  }
  viewport?.clamp({ direction: 'all', underflow: 'top' });

  if (rainSprite.value && lightningLayer.value) {
    rainSprite.value.width = PIXI_WORLD_SIZES_WIDTH;
    rainSprite.value.height = PIXI_WORLD_SIZES_HEIGHT;
  }

  if (!app.ticker.started) {
    app.render();
  }
});

const handleCloseModal = () => {
  if (selectedBuilding.value?.buildingKey === Buildings.Seller) {
    resetViewport();
  }

  if (!selectedBuilding.value && openedBuilding.value === Buildings.Seller) {
    store.openedBuilding = null;
  }

  if (app.ticker.started) {
    return;
  }

  app.ticker.start();
  isShowSellerModal.value = false;
};

const handleOpenModal = () => {
  if (!app.ticker.started) {
    return;
  }

  app.ticker.stop();
};

const waitForUserData = async (): Promise<void> => {
  stopWaitForUserData?.();
  if (store.userData) return;

  await new Promise<void>((resolve) => {
    stopWaitForUserData = watch(
      () => store.userData,
      (val) => {
        if (val) {
          stopWaitForUserData?.();
          resolve();
        }
      }
    );
  });
};

const openStorage = async () => {
  if (!store.userData) {
    await login();
    await waitForUserData();

    if (!store.userData) return;
  }

  const buildingObject = {
    buildingName: 'Storage',
    buildingHash: '',
    buildingKey: 'warehouse',
    descriptionKey: 'storage_description'
  };

  selectedBuilding.value = buildingObject as BuildingProps;
  buyTokenDescription.value = t(selectedBuilding.value.descriptionKey);

  isShowStorageModal.value = true;
};

const openStorageClickHandler = async () => {
  await openStorage();
};

const goToMiningBuildingByHash = async (tokenName: keyof typeof MINING_BUILDINGS | 'pearl' | 'blackPearl') => {
  if (tokenName === 'pearl' || tokenName === 'blackPearl') {
    const buildingData = focusMiningBuilding(Buildings.Dock);
    if (!buildingData) return;
    const data = buildingData;

    selectedBuilding.value = null;
    await nextTick();

    setBuyTokenBySelectedBuilding(Buildings.Dock as Buildings);

    viewport.animate({
      time: 1000,
      position: data.coords,
      ease: 'easeInOutSine',
      removeOnInterrupt: true
    });
    setTimeout(() => {
      selectedBuilding.value = {
        buildingName: '',
        buildingKey: Buildings.Dock,
        buildingHash: '',
        buildingId: '',
        descriptionKey: ''
      } as BuildingProps;
    }, 1100);

    return;
  }

  const buildingData = focusMiningBuilding(MINING_BUILDINGS[tokenName].buildingKey);
  if (!buildingData) return;
  const data = buildingData;

  selectedBuilding.value = null;
  await nextTick();

  setBuyTokenBySelectedBuilding(MINING_BUILDINGS[tokenName].buildingKey as Buildings);

  viewport.animate({
    time: 1000,
    position: data.coords,
    ease: 'easeInOutSine',
    removeOnInterrupt: true
  });
  setTimeout(() => {
    selectedBuilding.value = MINING_BUILDINGS[tokenName] as BuildingProps;
  }, 1100);
};

const focusMiningBuilding = (buildingKey: string) => {
  const found = interactiveObjects.value.find((item) => {
    const props = item?.tileObject?.properties;
    return Array.isArray(props) && props.some((p) => p.name === 'buildingKey' && p.value === buildingKey);
  });

  if (!found) return null;

  const sprite = found.spriteElement;
  const { x, y } = sprite.position;

  const centerX = x + sprite.width / 2;
  const centerY = y + sprite.height / 2;

  return {
    building: found,
    coords: { x: centerX, y: centerY }
  };
};

provide('showSellerModal', isShowSellerModal);
provide('sellerIsOpen', isSellerOpen);
provide('sellerData', sellerData);
provide('sellerStatusWS', statusWS);
provide('building', selectedBuilding);
provide('showSeasonModal', showSeasonModal);
provide('sellerModalStatus', modalStatus);
provide('goToBuildingByHash', goToBuildingByHash);
provide('goToBuildingByKey', goToBuildingByKey);
provide('openStorageClickHandler', openStorageClickHandler);
provide('goToMiningBuildingByHash', goToMiningBuildingByHash);
provide('showCraftingModal', showCraftingModal);
</script>

<style lang="scss">
.el-dialog.history-modal {
  max-width: 700px;
}

.history-modal {
  .el-dialog__body {
    max-width: unset;
    padding-top: 0px;
  }
}

.el-dialog.myProfileModal {
  min-width: 630px;

  .el-dialog__body {
    padding-top: 16px;
    padding-bottom: 16px;
  }

  @media screen and (max-width: 767px) {
    min-width: 350px;
  }
}
</style>

<style lang="scss" scoped>
header {
  height: 0px;
}

.tiledContainer {
  height: calc(100dvh - 117px);
  position: relative;

  .sidebar {
    position: absolute;
    right: 63px;
    top: 260px;
    height: fit-content;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;

    @media (max-width: 479px) {
      flex-direction: row;
      right: 20px;
      top: 15px;
    }

    .icon-item {
      cursor: pointer;
      width: 62px;
      height: 62px;
      border: 2px solid #ffffff;
      border-radius: 31px;
      background: radial-gradient(at 50% 180%, rgb(37, 78, 93) 40%, rgb(6, 21, 29) 60%);
      display: flex;
      align-items: center;
      justify-content: space-around;
      transition: border 0.2s;

      @media (max-width: 479px) {
        background: radial-gradient(at -0% -30%, rgb(37, 78, 93) 30%, rgb(6, 21, 29) 60%);
        border: 1px solid #ffffff;
        width: 56px;
        height: 56px;
      }

      &:hover {
        border: 2px solid #bc944d;

        @media (max-width: 479px) {
          border: 1px solid #bc944d;
        }
      }

      .icon-image {
        display: block;
        width: 70%;
        height: 70%;
      }
    }
  }
}

.scenesWrapper {
  width: 100%;
  -webkit-user-select: none;
  -khtml-user-select: none;
  -moz-user-select: none;
  -o-user-select: none;
  user-select: none;
}

.night-mode canvas {
  filter: brightness(0.7) contrast(1.1);
}

.overlay {
  position: absolute;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  opacity: 0;
  transition: 500ms ease all;
  mix-blend-mode: multiply;
}

.daytime-filter-overlay {
  opacity: 1;
  background-color: #1c0e9de0;
  mix-blend-mode: multiply;
  /* pointer-events: none; */
}

.scene-controls-panel {
  position: fixed;
  display: none;
  left: 0;
  top: 0;
  z-index: 999;
}

/** replace ugly fat windows scrollbars with small semi-transparent ones (firefox has nice scrollbars out of the box) */
::-webkit-scrollbar {
  opacity: 0.5;
  width: 6px;
  height: 6px;
}

::-webkit-scrollbar-track {
  background: rgba(22, 22, 30, 0.646);
}

::-webkit-scrollbar-track-piece {
  background-color: #1a232a6e;
}

::-webkit-scrollbar-thumb {
  background: rgb(58, 62, 84);
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: rgb(62, 65, 112);
}
</style>
